import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from './AuthShell';
import Toast from '../../components/forms/Toast';
import Icon from '../../components/common/Icon';
import { apiRequest, handleBranding, formatDate } from '../../utils';

/* Public, edition-gated self-serve "create a team" flow — free.
   Step 1: pick one of the edition's events.
   Step 2 (create): name + email -> "Set my password" emails a link, then the
     "instructions sent" screen. The team is created on the set-password page.
   Step 2 (login): existing account logs in and the team is created immediately.
   Backend: /api/create-team, /api/create-team/request-password. */
const CreateTeam = () => {
  const { t } = useTranslation();
  const { editionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [edition, setEdition] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [step, setStep] = useState(1);        // 1 | 2 | 'sent'
  const [mode, setMode] = useState('create'); // 'create' | 'login'
  const [eventId, setEventId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    handleBranding();
    (async () => {
      try {
        const data = await apiRequest('GET', `/create-team/${editionId}`);
        setEdition(data.edition);
        const evs = data.events || [];
        setEvents(evs);
        if (evs.length === 1) {
          setEventId(evs[0].id);
          setStep(2); // only one event — skip the picker
        }
      } catch (err) {
        setError(t(err.message, { defaultValue: err.message }));
        setShowToast(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [editionId, t]);

  const toast = (msg) => { setError(msg); setShowToast(true); };

  const goToAccount = (e) => {
    e.preventDefault();
    if (!eventId) { toast(t('selectAnEvent')); return; }
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === 'create') {
        await apiRequest('POST', '/create-team/request-password', {
          edition_id: editionId, event_id: eventId,
          first_name: firstName.trim(), last_name: lastName.trim(), email: email.trim(),
        });
        setStep('sent');
      } else {
        const data = await apiRequest('POST', '/create-team', {
          edition_id: editionId, event_id: eventId, email: email.trim(), password,
        });
        localStorage.setItem('isLoggedIn', '1');
        window.dispatchEvent(new Event('auth-changed'));
        navigate(data.redirect || '/team');
      }
    } catch (err) {
      if (err.message === 'useSetPassword') setMode('create');
      toast(t(err.message, { defaultValue: err.message }));
    } finally {
      setSubmitting(false);
    }
  };

  const closed = edition && !edition.open;
  const contactHref =
    `mailto:info@herculestrophy.com?subject=${encodeURIComponent(t('contactSubject'))}` +
    `&body=${encodeURIComponent(t('contactBody'))}`;

  const title = step === 'sent'
    ? t('instructionsSentTitle')
    : (edition ? edition.name : t('createTeam'));
  const subtitle = closed ? t('teamCreationClosed')
    : step === 1 ? t('createTeamSubtitle')
    : step === 'sent' ? ''
    : mode === 'login' ? t('logInToCreateTeam') : t('accountStepSubtitle');

  const freeNote = (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '.4rem',
      background: 'rgba(46, 204, 113, .12)', color: '#1e8e4f',
      borderRadius: '.5rem', padding: '.5rem .7rem', marginBottom: '1rem',
      fontSize: '.9rem', fontWeight: 600,
    }}>
      <Icon name="gift" /> {t('freeToBuild')}
    </div>
  );

  return (
    <AuthShell title={title} subtitle={subtitle}>
      {loading ? (
        <div className="auth__card"><span className="auth__spinner" /></div>
      ) : closed || !edition ? (
        <div className="auth__card">
          <p>{edition ? t('teamCreationClosed') : t('editionNotFound')}</p>
        </div>

      ) : step === 'sent' ? (
        <div className="auth__card">
          {freeNote}
          <p style={{ marginTop: 0 }}>{t('instructionsSentBody')}</p>
          <p style={{ fontSize: '.85rem', color: '#6b7280', marginBottom: '.75rem' }}>
            {t('didntGetEmail')}
          </p>
          <a className="auth__primary" href={contactHref} style={{ textDecoration: 'none' }}>
            <Icon name="envelope" /> {t('contactUs')}
          </a>
        </div>

      ) : step === 1 ? (
        <form className="auth__card" onSubmit={goToAccount}>
          {freeNote}
          <label className="auth__label">{t('chooseEvent')}</label>
          <p style={{ fontSize: '.8rem', color: '#6b7280', margin: '0 0 .6rem' }}>
            {t('changeLaterHint')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', marginBottom: '1rem' }}>
            {events.map((ev) => {
              const selected = eventId === ev.id;
              return (
                <button
                  type="button"
                  key={ev.id}
                  onClick={() => setEventId(ev.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '.7rem', textAlign: 'left',
                    padding: '.6rem .7rem', borderRadius: '.6rem',
                    border: `2px solid ${selected ? 'var(--primary-color, #eb5a40)' : 'rgba(0,0,0,.12)'}`,
                    background: selected ? 'rgba(235,90,64,.06)' : '#fff', cursor: 'pointer',
                  }}
                >
                  <div style={{
                    width: '3rem', height: '3rem', flexShrink: 0, borderRadius: '.5rem',
                    overflow: 'hidden', background: '#eef1f5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {ev.logo
                      ? <img src={ev.logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      : <Icon name="calendar" theme="secondary" />}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '.15rem', minWidth: 0 }}>
                    <span style={{ fontWeight: 600, color: '#1f2430' }}>{ev.name}</span>
                    {ev.date && (
                      <span style={{ fontSize: '.8rem', color: '#6b7280' }}>{formatDate(ev.date)}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          <button type="submit" className="auth__primary" disabled={!eventId}>
            <Icon name="arrow-right" /> {t('continue')}
          </button>
          <Toast showToast={showToast} setShowToast={setShowToast} message={error} />
        </form>

      ) : (
        <form className="auth__card" onSubmit={submit}>
          {freeNote}

          {mode === 'create' && (
            <div className="auth__row">
              <div className="auth__field">
                <label className="auth__label">{t('firstName')}</label>
                <div className="auth__input-wrap">
                  <input className="auth__input" type="text" value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t('enterFirstName')} autoComplete="given-name" required />
                </div>
              </div>
              <div className="auth__field">
                <label className="auth__label">{t('lastName')}</label>
                <div className="auth__input-wrap">
                  <input className="auth__input" type="text" value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={t('enterLastName')} autoComplete="family-name" required />
                </div>
              </div>
            </div>
          )}

          <div className="auth__field">
            <label className="auth__label">{t('email')}</label>
            <div className="auth__input-wrap">
              <input className="auth__input" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('businessEmailPlaceholder')} autoComplete="email" required />
            </div>
          </div>

          {mode === 'login' && (
            <div className="auth__field">
              <label className="auth__label">{t('password')}</label>
              <div className="auth__input-wrap">
                <input className="auth__input" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('enterPassword')} autoComplete="current-password" required />
              </div>
              <Link to="/forgot-password" className="auth__forgot">{t('forgotPassword')}</Link>
            </div>
          )}

          <button type="submit" className="auth__primary" disabled={submitting}>
            {submitting ? <span className="auth__spinner" />
              : <Icon name={mode === 'login' ? 'users' : 'lock'} />}
            {mode === 'login' ? t('createMyTeamFree') : t('setMyPassword')}
          </button>

          <button type="button" className="auth__forgot"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={() => setMode(mode === 'login' ? 'create' : 'login')}>
            {mode === 'login' ? t('createNewAccount') : t('havePasswordLogin')}
          </button>

          <Toast showToast={showToast} setShowToast={setShowToast} message={error} />
        </form>
      )}
    </AuthShell>
  );
};

export default CreateTeam;
